import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type Table as TanStackTable,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ChevronRight, TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { DataTablePagination } from "@/components/ui/table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type ConfigurationItem } from "../..";

interface ListProps {
  configurationItems?: ConfigurationItem[];
  errorMessage?: string;
  isFetching?: boolean;
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 20;
const EMPTY_VALUE = "-";

interface ConfigurationColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type ConfigurationColumnDef = ColumnDef<ConfigurationItem, unknown> & {
  meta?: ConfigurationColumnMeta;
};

const getConfigurationItemKey = (item: ConfigurationItem, index: number) =>
  item.resource || item.frontend_endpoint || item.name || `configuration-item-${index}`;

const formatCount = (count: number | null | undefined) =>
  typeof count === "number" && Number.isFinite(count) ? count : EMPTY_VALUE;

const getDescription = (description: string | null | undefined) => description?.trim();

const getColumnMeta = (columnDef: ColumnDef<ConfigurationItem, unknown>) =>
  columnDef.meta as ConfigurationColumnMeta | undefined;

const createColumns = (
  t: TFunction,
  onClickView: (item: ConfigurationItem) => void
): ConfigurationColumnDef[] => [
  {
    id: "actions",
    header: () => <span className="sr-only">{t("common.view")}</span>,
    cell: ({ row }) => (
      <WFPButton
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        disabled={!row.original.frontend_endpoint}
        onClick={() => onClickView(row.original)}
      >
        {t("common.view")}
        <ChevronRight className="size-4" aria-hidden="true" />
      </WFPButton>
    ),
    meta: {
      cellClassName: "w-0 whitespace-nowrap align-top",
      headerClassName: "w-0",
    },
  },
  {
    id: "name",
    accessorFn: row => row.name,
    header: () => t("configuration.list.column.typeOfSetting"),
    cell: ({ row }) => row.original.name || EMPTY_VALUE,
    meta: {
      cellClassName: "min-w-64 whitespace-normal align-top text-foreground leading-5",
      headerClassName: "min-w-64",
    },
  },
  {
    id: "count",
    accessorFn: row => row.count,
    header: () => t("configuration.list.column.number"),
    cell: ({ row }) => (
      <span className="inline-flex min-w-8 justify-center rounded-md bg-secondary px-2 py-1 text-foreground text-xs">
        {formatCount(row.original.count)}
      </span>
    ),
    meta: {
      cellClassName: "max-w-32 text-right align-top tabular-nums",
      headerClassName: "max-w-32 text-right",
    },
  },
  {
    id: "description",
    accessorFn: row => getDescription(row.description),
    header: () => t("configuration.list.column.description"),
    cell: ({ row }) => getDescription(row.original.description) || EMPTY_VALUE,
    meta: {
      cellClassName: "whitespace-normal text-muted-foreground leading-5",
    },
  },
];

const ConfigurationDataTable = ({
  emptyMessage,
  isLoading,
  table,
}: {
  emptyMessage: string;
  isLoading: boolean;
  table: TanStackTable<ConfigurationItem>;
}) => {
  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <TableHead
                key={header.id}
                className={getColumnMeta(header.column.columnDef)?.headerClassName}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={columnCount}>
              <Loading isVisible small />
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : null}
        {!isLoading && rows.length
          ? rows.map(row => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
  );
};

export const List = ({
  configurationItems = [],
  errorMessage = "",
  isFetching = false,
  pageSize = DEFAULT_PAGE_SIZE,
}: ListProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const resolvedPageSize = pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
  const hasConfigurationItems = configurationItems.length > 0;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: resolvedPageSize,
  });

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageSize === resolvedPageSize
        ? currentPagination
        : { pageIndex: 0, pageSize: resolvedPageSize }
    );
  }, [resolvedPageSize]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(configurationItems.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [configurationItems.length]);

  const onClickView = useCallback(
    (item: ConfigurationItem) => {
      if (!item.frontend_endpoint) {
        return;
      }

      history.push(item.frontend_endpoint);
    },
    [history]
  );

  const columns = useMemo(() => createColumns(t, onClickView), [onClickView, t]);
  const table = useReactTable({
    columns,
    data: configurationItems,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row, index) => getConfigurationItemKey(row, index),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-md border border-border bg-background">
        <ConfigurationDataTable
          emptyMessage={t("configuration.list.empty", {
            defaultValue: "No configuration items found",
          })}
          isLoading={isFetching}
          table={table}
        />
        {hasConfigurationItems ? <DataTablePagination table={table} /> : null}
      </div>
    </div>
  );
};

export default List;
